import { AnalyticsEventName } from "@albertine/analytics-client";
import { useApiClient, useGet, usePost } from "@albertine/api-client";
import type { RecommendationItem } from "@albertine/types/openapi/lmt";
import { Loader2, RefreshCw, Sparkles, WandSparkles } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const requestRecommendationsQueryKey = (requestId: string) =>
    ["requestRecommendations", { param: { id: requestId } }] as const;

export interface RecommendButtonProps {
    requestId: string;
    hasRequestLocation: boolean;
    onOpenRecommendations?: (items: RecommendationItem[]) => void;
    onRecommendationsLoaded?: (items: RecommendationItem[]) => void;
    className?: string;
}

const IDLE_BUTTON_LABEL = "Recommendations";
const IDLE_TOOLTIP_NO_LOCATION = "Add a location to the request to get recommendations";
const READY_PRIMARY_LABEL = "See options";

const DOT_INTERVAL_MS = 280;

export function LoadingEllipsis() {
    const [step, setStep] = useState(0);

    useEffect(function runEllipsisDotCycle() {
        const id = window.setInterval(() => {
            setStep((s) => (s + 1) % 4);
        }, DOT_INTERVAL_MS);

        return () => window.clearInterval(id);
    }, []);

    return (
        <span className="relative inline-block w-[1.5ch]" aria-hidden>
            {[0, 1, 2].map((i) => renderEllipsisDot(i, step))}
        </span>
    );
}

function renderEllipsisDot(i: number, step: number) {
    return (
        <span
            key={i}
            style={{
                opacity: step > i ? 1 : 0,
                transition: "opacity 180ms ease-in-out",
            }}
        >
            .
        </span>
    );
}

const LOADING_STAGE_LABELS = ["Still searching", "Almost there", "Hang tight"] as const;

const LOADING_FIRST_STAGE_LABEL = "Finding options";

const LOADING_STAGE_INTERVAL_MS = 5000;

export function LoadingRecommendButton({ className }: { className?: string }) {
    const fetchStartedAt = Date.now();
    const [now, setNow] = useState(Date.now);

    useEffect(function subscribeLoadingElapsedClock() {
        const id = window.setInterval(() => {
            setNow(Date.now());
        }, 400);

        return () => window.clearInterval(id);
    }, []);

    const elapsed = Math.max(0, now - fetchStartedAt);
    const rawStage = Math.floor(elapsed / LOADING_STAGE_INTERVAL_MS);
    const stageIndex = Math.min(rawStage, LOADING_STAGE_LABELS.length);
    const label =
        stageIndex === 0
            ? LOADING_FIRST_STAGE_LABEL
            : LOADING_STAGE_LABELS[stageIndex - 1];

    return (
        <Button
            type="button"
            variant="ghost"
            disabled
            className={cn(
                "w-full gap-2 bg-blue-100/50 text-blue-600 hover:bg-blue-100 hover:text-blue-600",
                className,
            )}
            aria-live="polite"
            aria-busy
        >
            <Loader2 className="size-4 shrink-0 animate-spin" />
            <span className="flex items-center">
                {label}
                <LoadingEllipsis />
            </span>
        </Button>
    );
}

function ReadyRecommendationsRow({
    recommendationCount,
    onOpen,
    onGenerate,
    className,
}: {
    recommendationCount: number;
    onOpen: () => void;
    onGenerate: () => void;
    className?: string;
}) {
    return (
        <div className={cn("flex w-full items-center gap-1.5", className)}>
            <Button
                type="button"
                variant="ghost"
                className="min-w-0 flex-1 gap-1.5 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-600"
                onClick={onOpen}
            >
                <Sparkles className="size-4 shrink-0" />
                <span className="truncate">
                    {READY_PRIMARY_LABEL} ({recommendationCount})
                </span>
            </Button>
            <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="shrink-0"
                aria-label="Generate new recommendations"
                onClick={onGenerate}
            >
                <RefreshCw className="size-3.5" />
            </Button>
        </div>
    );
}

export function IdleRecommendationsButton({
    className,
    disabled,
    onClick,
}: {
    className?: string;
    disabled?: boolean;
    onClick?: () => void;
}) {
    const button = (
        <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={onClick}
            className={cn("w-full cursor-pointer justify-center gap-1.5", className)}
        >
            <WandSparkles className="size-4" />
            {IDLE_BUTTON_LABEL}
        </Button>
    );

    if (!disabled) {
        return button;
    }

    return (
        <Tooltip>
            <TooltipTrigger
                render={<span className="inline-block w-full">{button}</span>}
            />
            <TooltipContent side="top" align="start">
                {IDLE_TOOLTIP_NO_LOCATION}
            </TooltipContent>
        </Tooltip>
    );
}

export function RecommendButton({
    requestId,
    hasRequestLocation,
    onOpenRecommendations,
    onRecommendationsLoaded,
    className,
}: RecommendButtonProps) {
    const { queryClient } = useApiClient();
    const posthog = usePostHog();

    const readParams = { param: { id: requestId } } as const;

    const {
        data: readData,
        isLoading: isReadLoading,
        isFetching: isReadFetching,
        isError: isReadError,
        refetch: refetchRead,
    } = useGet("requestRecommendations", readParams, {
        enabled: hasRequestLocation && Boolean(requestId),
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const generateMutation = usePost("requestRecommendationsGenerate", {
        mutationKey: [...requestRecommendationsQueryKey(requestId), "post"],
        onSuccess: (data, variables) => {
            queryClient.setQueryData(
                requestRecommendationsQueryKey(variables.param.id),
                data,
            );
        },
    });

    const items: RecommendationItem[] | undefined = readData?.items;
    const recommendationCount = items?.length ?? 0;

    useEffect(() => {
        if (!items?.length) {
            return;
        }

        onRecommendationsLoaded?.(items);
    }, [items, onRecommendationsLoaded]);

    const busy =
        hasRequestLocation &&
        (isReadLoading ||
            generateMutation.isPending ||
            (isReadError && isReadFetching));

    const showError =
        hasRequestLocation && !busy && (isReadError || generateMutation.isError);

    const showReady =
        hasRequestLocation && !busy && !showError && recommendationCount > 0;

    if (busy) {
        return <LoadingRecommendButton className={className} />;
    }

    if (showReady && items) {
        return (
            <ReadyRecommendationsRow
                recommendationCount={recommendationCount}
                onOpen={() => onOpenRecommendations?.(items)}
                onGenerate={() => {
                    posthog?.capture(
                        AnalyticsEventName.REQUEST_RECOMMENDATIONS_REGENERATED,
                        {
                            request_id: requestId,
                        },
                    );
                    generateMutation.mutate({ param: { id: requestId } });
                }}
                className={className}
            />
        );
    }

    if (showError) {
        return (
            <Button
                type="button"
                variant="outline"
                className={cn("w-full gap-1.5 border-red-200 text-red-700", className)}
                onClick={() =>
                    generateMutation.isError
                        ? generateMutation.mutate({ param: { id: requestId } })
                        : void refetchRead()
                }
            >
                <WandSparkles className="size-4" />
                Try again
            </Button>
        );
    }

    if (!hasRequestLocation) {
        return <IdleRecommendationsButton className={className} disabled />;
    }

    return (
        <IdleRecommendationsButton
            className={className}
            onClick={() => {
                posthog?.capture(AnalyticsEventName.REQUEST_RECOMMENDATIONS_TRIGGERED, {
                    request_id: requestId,
                    category: "idle",
                });
                generateMutation.mutate({ param: { id: requestId } });
            }}
        />
    );
}
